/* eslint-disable react-hooks/exhaustive-deps */
import { Select } from '@/components/input'
import { Search } from '@/components/search'
import { PlayerItemLoading } from '@/components/skeleton'
import { LIMIT, OFFSET } from '@/constants/Common'
import { clubRankingsOptions } from '@/constants/DropdownOptionsConstants'
import { useDebounce } from '@/hooks/useDebounce'
import { ClubRankingItem } from '@/pages/club/components/ClubRankingItem'
import { useGetClubRankings } from '@/services'
import { ClubMemberRanking } from '@/types'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

export const ClubRankings = () => {
  const { t } = useTranslation()

  const params = useParams()

  const { clubId } = params

  const [memberSearchText, setMemberSearchText] = useState('')
  const [sortParam, setSortParam] = useState<string>('doubles')
  const [offset, setOffset] = useState(OFFSET)
  const [clubMemberRankings, setClubMemberRankings] = useState<Map<number, ClubMemberRanking>>(new Map())
  const [lastElement, setLastElement] = useState<any>(null)

  const debouncedMemberSearchText = useDebounce<string>(memberSearchText || '*', 200)

  const { data: resClubRankings, isLoading } = useGetClubRankings(
    Number(clubId) || 0,
    LIMIT,
    offset,
    debouncedMemberSearchText || '*',
    sortParam
  )

  const observer = useRef(
    new IntersectionObserver((entries) => {
      const first = entries[0]
      if (first.isIntersecting) {
        setOffset((pre) => pre + LIMIT)
      }
    })
  )

  const onChangeMemberSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMemberSearchText(e.target.value)
    setOffset(OFFSET)
  }

  const onChangeSortParam = (val: string) => {
    if (val !== sortParam) {
      setClubMemberRankings(new Map())
    }
    setSortParam(val)
    setOffset(OFFSET)
  }

  useEffect(() => {
    if (resClubRankings)
      setClubMemberRankings((old) => {
        let temp: Map<number, ClubMemberRanking>
        if (offset === OFFSET) {
          temp = new Map()
        } else {
          temp = old
        }

        resClubRankings.result?.memberRanking?.hits.forEach((mem) => {
          temp.set(mem.id, mem)
        })

        return temp
      })
  }, [resClubRankings])

  useEffect(() => {
    const currentElement = lastElement
    const currentObserver = observer.current

    if (currentElement) {
      currentObserver.observe(currentElement)
    }

    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement)
      }
    }
  }, [lastElement])

  return (
    <>
      <div className="flex flex-col gap-y-8">
        <div className="flex flex-col gap-y-3">
          {/* <h4 className="text-xl text-white font-semibold">{t('common.Rankings')}</h4> */}

          <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between">
            <Search
              searchText={memberSearchText}
              onChangeInput={onChangeMemberSearch}
              className="w-full md:w-56 py-2 px-3 gap-x-2"
            />

            <Select
              placeholder={t('common.select')}
              options={clubRankingsOptions}
              value={clubRankingsOptions.find((option) => option.value === sortParam)}
              onChange={onChangeSortParam}
              containerClassName="!w-40"
              optionsClassName="!rounded"
              buttonClassName="!bg-primary-500 !border-none !py-1 !text-white !font-semibold"
              optionClassName="!text-navy-900 !px-4"
              activeOptionClassName="!bg-primary-100"
            />
          </div>
        </div>

        <div className="flex flex-col gap-y-2">
          {Array.from(clubMemberRankings.values())?.map((item, index: number) => {
            return index === clubMemberRankings.size - 1 && resClubRankings?.result?.memberRanking?.hasMore ? (
              <div key={item.id} ref={setLastElement}>
                <ClubRankingItem member={item} ratingType={sortParam} />
              </div>
            ) : (
              <ClubRankingItem key={item.id} member={item} ratingType={sortParam} />
            )
          })}

          {isLoading && (
            <>
              <PlayerItemLoading className="!max-w-[964px]" />
              <PlayerItemLoading className="!max-w-[964px]" />
              <PlayerItemLoading className="!max-w-[964px]" />
              <PlayerItemLoading className="!max-w-[964px]" />
              <PlayerItemLoading className="!max-w-[964px]" />
              <PlayerItemLoading className="!max-w-[964px]" />
            </>
          )}
          {resClubRankings && !resClubRankings?.result?.memberRanking?.hasMore && (
            <div className="flex justify-center">
              <p className="text-slate-300 text-base font-semibold">{t('common.noMoreResults')}</p>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
